import React, { useCallback, useEffect, useMemo, useState } from 'react';

import i18n from '@nextroll/ar-i18n';

import { IWorkflowAccountActionContactFilter } from 'types/roflWorkflowActions';
import { WORKFLOW_ACCOUNT_ACTION_CONTACT_FILTER_BASE } from 'util/workflow.constants';

import { Chips } from '../../../basic/Chips/Chips';
import { ExpandablePanelSeniorityFilter } from '../../../basic/ExpandablePanel/ExpandablePanelSeniorityFilter';
import { ExpandablePanelContactInfoFilter } from '../../../basic/ExpandablePanel/ExpandablePanelContactInfoFilter';
import { ExpandablePanelLocationFilter } from '../../../basic/ExpandablePanel/ExpandablePanelLocationFilter';
import { ExpandablePanelJobTitleFilter } from '../../../basic/ExpandablePanel/ExpandablePanelJobTitleFilter';
import { ExpandablePanelContactEngageFilter } from '../../../basic/ExpandablePanel/ExpandablePanelContactEngageFilter';


const FIND_JOB_TITLES = (excluded?: boolean) => (rule: IWorkflowAccountActionContactFilter) => rule.attribute_name === 'job_title' && rule.operator === (excluded ? 'not_contains' : 'contains') && rule.value;

interface IProps {
    rules?: (IWorkflowAccountActionContactFilter | { rules: IWorkflowAccountActionContactFilter[] })[]; // it can be nested some time
    onUpdateRules?: (rules: IWorkflowAccountActionContactFilter[]) => void;
    activityQuery?: boolean;
    onUpdateActivityQuery?: (activityQuery: boolean) => void;
}

export const AccountActionCrmContactFilterSection = (props: IProps) => {
    const { rules, onUpdateRules, activityQuery, onUpdateActivityQuery } = props;

    const allRules = useMemo(() => (rules || [])
        .map(rule => (rule as { rules: IWorkflowAccountActionContactFilter[] }).rules || rule)
        .flat(), [rules]);

    // both included and excluded job title filters
    const [jobTitleInFilters, setJobTitleInFilters] = useState(allRules.map(FIND_JOB_TITLES(false)).filter(Boolean));
    const [jobTitleExFilters, setJobTitleExFilters] = useState(allRules.map(FIND_JOB_TITLES(true)).filter(Boolean));

    // other filter contents
    const [seniorityFilters, setSiniorityFilters] = useState(allRules.find(rule => rule.attribute_name === 'seniority_level')?.values || []);
    const [locationFilters, setLocationFilters] = useState(allRules.find(rule => rule.attribute_name === 'personal_state')?.values || []);
    const [contactInfoFilters, setContactInfoFilters] = useState(allRules.find(rule => rule.attribute_name === 'mobile_phone') ? ['checked'] : []);
    const [contactEngageFilters, setContactEngageFilters] = useState(activityQuery ? ['activityQuery'] : []);

    /**
     * convert filter values to the rule-list structure that is recognized by the POST request
     */
    useEffect(() => {
        const updatedRules = [
            seniorityFilters.length > 0 && {
                ...WORKFLOW_ACCOUNT_ACTION_CONTACT_FILTER_BASE,
                attribute_rule_index: 0,
                attribute_name: 'seniority_level',
                display_name: 'Seniority Level',
                operator: 'in',
                values: seniorityFilters,
            },
            locationFilters.length > 0 && {
                rule_type: 'OR',
                rules: [
                    {
                        ...WORKFLOW_ACCOUNT_ACTION_CONTACT_FILTER_BASE,
                        attribute_rule_index: 1,
                        attribute_name: 'personal_state',
                        display_name: 'Personal State',
                        operator: 'in',
                        values: locationFilters,
                    },
                    {
                        ...WORKFLOW_ACCOUNT_ACTION_CONTACT_FILTER_BASE,
                        attribute_rule_index: 2,
                        attribute_name: 'professional_state',
                        display_name: 'Professional State',
                        operator: 'in',
                        values: locationFilters,
                    },
                ],
            },
            contactInfoFilters.length > 0 && {
                rule_type: 'OR',
                rules: [
                    {
                        ...WORKFLOW_ACCOUNT_ACTION_CONTACT_FILTER_BASE,
                        attribute_rule_index: 3,
                        attribute_name: 'mobile_phone',
                        display_name: 'Mobile Phone',
                        operator: 'exists',
                        multivalued: true,
                    },
                    {
                        ...WORKFLOW_ACCOUNT_ACTION_CONTACT_FILTER_BASE,
                        attribute_rule_index: 4,
                        attribute_name: 'direct_number',
                        display_name: 'Direct Number',
                        operator: 'exists',
                        multivalued: true,
                    },
                ],
            },
            jobTitleInFilters.length > 0 && {
                rule_type: 'OR',
                rules: jobTitleInFilters
                    .filter(title => (title || '').length > 2) // ignore included job titles that contain only 1 or 2 chars
                    .map((title, i) => ({
                        ...WORKFLOW_ACCOUNT_ACTION_CONTACT_FILTER_BASE,
                        attribute_rule_index: 5 + i,
                        attribute_name: 'job_title',
                        display_name: 'Job Title',
                        operator: 'contains',
                        value: title,
                    })),
            },
            jobTitleExFilters.length > 0 && {
                rule_type: 'AND',
                rules: jobTitleExFilters
                    .filter(title => (title || '').length > 2) // ignore excluded job titles that contain only 1 or 2 chars
                    .map((title, i) => ({
                        ...WORKFLOW_ACCOUNT_ACTION_CONTACT_FILTER_BASE,
                        attribute_rule_index: 5 + jobTitleInFilters.length + i,
                        attribute_name: 'job_title',
                        display_name: 'Job Title',
                        operator: 'not_contains',
                        value: title,
                    })),
            },
        ].filter(Boolean) as IWorkflowAccountActionContactFilter[];

        // update the latest contact filter rules to the parent
        onUpdateRules?.(updatedRules);
    }, [
        jobTitleInFilters, jobTitleExFilters,
        seniorityFilters, locationFilters, contactInfoFilters, onUpdateRules,
    ]);

    /**
     * update the parent activity query value only if the checkbox is updated
     */
    useEffect(() => {
        const updatedActivityQuery = contactEngageFilters.length > 0; // ['activityQuery']
        updatedActivityQuery !== activityQuery && onUpdateActivityQuery?.(updatedActivityQuery);
    }, [contactEngageFilters, activityQuery, onUpdateActivityQuery]);

    /**
     * total number of applied filters; such a number is displayed in the section title if not 0
     */
    const total = useMemo(
        () => [
            jobTitleInFilters, jobTitleExFilters,
            seniorityFilters, locationFilters, contactInfoFilters, contactEngageFilters,
        ].reduce((sum, f) => sum + f.length, 0),
        [
            jobTitleInFilters, jobTitleExFilters,
            seniorityFilters, locationFilters,
            contactInfoFilters, contactEngageFilters,
        ],
    );

    const handleClearContactFilters = useCallback(() => {
        if (total === 0) {
            return; // already cleared; do nothing
        }
        setJobTitleInFilters([]);
        setJobTitleExFilters([]);
        setSiniorityFilters([]);
        setLocationFilters([]);
        setContactInfoFilters([]);
        setContactEngageFilters([]);
    }, [
        total,
        setJobTitleInFilters,
        setJobTitleExFilters,
        setSiniorityFilters,
        setLocationFilters,
        setContactInfoFilters,
        setContactEngageFilters,
    ]);

    return (
        <div className='workflow-node-panel-filter-group ar-checklist'>
            <div className='workflow-node-panel-filter-group-title'>
                <h2>{i18n.gettext('Contact Filters')}</h2>
                <Chips labels={[`${total}`]} />
                <div className='workflow-node-panel-filter-group-chip-divider' />
                <button
                    type='button'
                    className={`btn btn-link${total === 0 ? ' btn-link-disabled' : ''}`}
                    onClick={handleClearContactFilters}
                >
                    {i18n.gettext('Clear All')}
                </button>
            </div>

            <h4 className='mt-4'>{i18n.gettext('Persona Attributes')}</h4>
            <ExpandablePanelJobTitleFilter
                includedFilters={jobTitleInFilters as string[]}
                setIncludedFilters={setJobTitleInFilters}
                excludedFilters={jobTitleExFilters as string[]}
                setExcludedFilters={setJobTitleExFilters}
            />
            <ExpandablePanelSeniorityFilter filters={seniorityFilters} setFilters={setSiniorityFilters} />
            <ExpandablePanelLocationFilter filters={locationFilters} setFilters={setLocationFilters} />
            <ExpandablePanelContactInfoFilter filters={contactInfoFilters} setFilters={setContactInfoFilters} />

            <h4 className='mt-4'>{i18n.gettext('Sales Signals')}</h4>
            <ExpandablePanelContactEngageFilter
                filters={contactEngageFilters}
                setFilters={setContactEngageFilters}
            />
            <br />
            <p>
                {
                    i18n.gettext('Note: Mobile numbers provided have not been screened against the national '
                    + '<DoNotCallLink>Do Not Call</DoNotCallLink> registry. When ready to use this number, '
                    + 'we recommend confirming the number is not registered to avoid potential '
                    + '<FinesLink>fines under U.S. law.</FinesLink>',
                    {
                        DoNotCallLink: (
                            <a
                                href='https://www.donotcall.gov/'
                                target='_blank'
                                rel='noopener noreferrer'
                            />
                        ),
                        FinesLink: (
                            <a
                                href='https://consumer.ftc.gov/articles/national-do-not-call-registry-faqs#:~:text=What%E2%80%99s%20the%20penalty,50%2C120%20per%20call'
                                target='_blank'
                                rel='noopener noreferrer'
                            />
                        ),
                    })
                }
            </p>
        </div>
    );
};
